import { useState } from "react";
import { type CodeResponse, useGoogleLogin } from "@react-oauth/google";
import { useAuth } from "@shared/contexts/auth-context";
import { useToast } from "@shared/helper_lib/hooks/use-toast";
import { Button } from "@shared/ui/components/button";
import { Checkbox } from "@shared/ui/components/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@shared/ui/components/dialog";
import { GoogleStyleButton } from "@shared/ui/components/google-style-button";
import { GoogleIcon, TelegramIcon, YandexIcon } from "@shared/ui/icons";

import { useTelegramAuth, useYandexAuth } from "../hooks";

export function AuthModal(): JSX.Element {
  const { authModalOpen, closeAuthModal, authModalMode, openAuthModal, signInWithGoogleCode } = useAuth();
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [agreedToOffer, setAgreedToOffer] = useState(true);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(true);
  const { toast } = useToast();

  const consents =
    authModalMode === "signup" && agreedToOffer && agreedToPrivacy ? { agreedToOffer, agreedToPrivacy } : undefined;

  const yandexAuth = useYandexAuth(consents);
  const telegramAuth = useTelegramAuth(consents, openAuthModal);

  const canProceed = authModalMode === "signin" || (agreedToOffer && agreedToPrivacy);

  const googleLogin = useGoogleLogin({
    onSuccess: async (codeResponse: CodeResponse) => {
      if (!canProceed) {
        toast({
          title: "Необходимо согласие",
          description: "Пожалуйста, примите условия оферты и политику конфиденциальности",
          variant: "destructive"
        });
        return;
      }

      try {
        setIsGoogleLoading(true);
        await signInWithGoogleCode(codeResponse.code, consents);
      } catch (error) {
        console.error("Google auth failed:", error);

        let errorMessage = "Ошибка при авторизации через Google";

        if (typeof error === "object" && error !== null && "response" in error) {
          const response = (error as { response?: { data?: { message?: string | string[] } } }).response;
          const serverMessage = response?.data?.message;

          const messageText = Array.isArray(serverMessage) ? serverMessage[0] : serverMessage;

          if (
            messageText ===
            "Регистрация новых пользователей через Google недоступна. Используйте вход через Яндекс или Telegram."
          ) {
            toast({
              title: "Вход через Google недоступен",
              description: "Этот аккаунт не зарегистрирован. Войдите через Яндекс или Telegram.",
              variant: "destructive"
            });
            setIsGoogleLoading(false);
            return;
          }

          if (messageText === "Необходимо принять условия оферты и политику конфиденциальности") {
            openAuthModal("signup");

            toast({
              title: "Аккаунт не найден",
              description: "Пожалуйста, зарегистрируйтесь, приняв условия оферты и политику конфиденциальности",
              variant: "destructive"
            });
            setIsGoogleLoading(false);
            return;
          }

          errorMessage = messageText || errorMessage;
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }

        toast({
          title: "Ошибка авторизации",
          description: errorMessage,
          variant: "destructive"
        });
      } finally {
        setIsGoogleLoading(false);
      }
    },
    onError: () => {
      console.error("Google login failed");
      toast({
        title: "Ошибка авторизации",
        description: "Не удалось выполнить вход через Google. Пожалуйста, попробуйте снова",
        variant: "destructive"
      });
      setIsGoogleLoading(false);
    },
    flow: "auth-code"
  });

  const handleOAuthClick = (provider: () => void) => {
    if (!canProceed) {
      toast({
        title: "Необходимо согласие",
        description: "Пожалуйста, примите условия оферты и политику конфиденциальности",
        variant: "destructive"
      });
      return;
    }
    provider();
  };

  const toggleMode = () => {
    if (authModalMode === "signin") {
      openAuthModal("signup");
    } else {
      openAuthModal("signin");
    }
  };

  return (
    <Dialog open={authModalOpen} onOpenChange={closeAuthModal}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            {authModalMode === "signin" ? "Вход в аккаунт" : "Создайте аккаунт"}
          </DialogTitle>
          <DialogDescription>
            {authModalMode === "signin" ? "Войдите в свой аккаунт для доступа к истории транскрипций" : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          {authModalMode === "signin" && (
            <GoogleStyleButton
              icon={<GoogleIcon />}
              label="Вход через аккаунт Google"
              onClick={() => handleOAuthClick(() => googleLogin())}
              isLoading={isGoogleLoading}
              iconSize="h-7 w-7"
            />
          )}

          <GoogleStyleButton
            icon={<YandexIcon />}
            label="Вход через аккаунт Яндекс"
            onClick={() => handleOAuthClick(yandexAuth.handleAuth)}
            isLoading={yandexAuth.isLoading}
            iconSize="h-7 w-7"
          />

          <GoogleStyleButton
            icon={<TelegramIcon />}
            label="Вход через аккаунт Telegram"
            onClick={() => handleOAuthClick(telegramAuth.handleAuth)}
            isLoading={telegramAuth.isLoading}
            iconSize="h-7 w-7"
          />

          {authModalMode === "signup" && (
            <div className="space-y-3 pt-4 border-t">
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="offer"
                  checked={agreedToOffer}
                  onCheckedChange={checked => setAgreedToOffer(checked === true)}
                />
                <label htmlFor="offer" className="text-sm leading-relaxed cursor-pointer">
                  Я принимаю{" "}
                  <a
                    href="/offer"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 underline"
                    onClick={e => e.stopPropagation()}
                  >
                    условия оферты
                  </a>
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <Checkbox
                  id="privacy"
                  checked={agreedToPrivacy}
                  onCheckedChange={checked => setAgreedToPrivacy(checked === true)}
                />
                <label htmlFor="privacy" className="text-sm leading-relaxed cursor-pointer">
                  Я принимаю{" "}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 underline"
                    onClick={e => e.stopPropagation()}
                  >
                    политику конфиденциальности
                  </a>
                </label>
              </div>
            </div>
          )}

          <div className="pt-4 text-center text-sm">
            {authModalMode === "signin" ? (
              <Button variant="link" onClick={toggleMode} className="text-sm">
                Нет аккаунта? Зарегистрироваться
              </Button>
            ) : (
              <Button variant="link" onClick={toggleMode} className="text-sm">
                Уже зарегистрированы? Войти
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
